import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import { createClient, listClients, normalizeClient, Client } from '@/services/clientsService';
import type { RootState } from '@/store/index';

const adapter = createEntityAdapter<Client>();

const initial = adapter.getInitialState({
  status: 'idle' as 'idle' | 'loading' | 'succeeded' | 'failed',
  total: 0,
  error: null as null | string,
});

// Thunks use the service which already normalizes
export const fetchClientsThunk = createAsyncThunk('clients/fetch', async (params: any = {}) => {
  console.log('🚀 fetchClientsThunk STARTED');
  const result = await listClients(params); // -> {items,total}
  console.log('🚀 fetchClientsThunk RESULT:', result);
  return result;
});

export const createClientThunk = createAsyncThunk('clients/create', async (payload: any) => {
  return await createClient(payload); // -> normalized client
});

const slice = createSlice({
  name: 'clients',
  initialState: initial,
  reducers: {
    // optimistic insert/upsert; payload can be raw, we normalize here again
    upsertClient: (state, action) => {
      const c = normalizeClient(action.payload);
      if (c?.id) adapter.upsertOne(state, c);
    },
    clearClients: state => {
      adapter.removeAll(state);
      state.total = 0;
    },
  },
  extraReducers: b => {
    b.addCase(fetchClientsThunk.pending, s => {
      console.log('🔥 Redux fetchClientsThunk.pending');
      s.status = 'loading';
      s.error = null;
    });
    b.addCase(fetchClientsThunk.fulfilled, (s, a) => {
      console.log('🔥 Redux fetchClientsThunk.fulfilled:', a.payload);
      try {
        const items = (a.payload?.items ?? []).map(normalizeClient).filter((x: any) => x?.id);
        console.log('🔥 Redux after normalize & filter:', items);
        console.log('🔥 Redux items length:', items.length);
        console.log('🔥 Redux first item:', items[0]);
        adapter.setAll(s, items);
        s.total = a.payload?.total ?? items.length;
        s.status = 'succeeded';
        console.log('🔥 Redux state after setAll:', s);
      } catch (err) {
        console.error('🔥 Redux ERROR in fulfilled:', err);
      }
    });
    b.addCase(fetchClientsThunk.rejected, (s, a) => {
      console.log('🔥 Redux fetchClientsThunk.rejected:', a.error);
      s.status = 'failed';
      s.error = String(a.error?.message || 'fetch failed');
    });

    b.addCase(createClientThunk.fulfilled, (s, a) => {
      const c = normalizeClient(a.payload);
      if (c?.id) adapter.upsertOne(s, c);
    });
  },
});

export const { upsertClient, clearClients } = slice.actions;
export default slice.reducer;

// Selectors – ALWAYS from state.domain.clients (canonical)
export const selectClientsState = (state: any) => state?.domain?.clients ?? initial;

export const clientsSelectors = adapter.getSelectors<any>(selectClientsState);
